import { Colors } from "../../config/DesignConstants";
import Entity from "../../core/entity/Entity";
import { angleDelta, clamp, polarToVec } from "../../core/util/MathUtil";
import {
  createRegularPolygon,
  PolygonShape,
} from "../../core/util/PolygonUtils";
import { V, V2d } from "../../core/Vector";
import VectorSprite from "../graphics/VectorSprite";
import { LevelEntity } from "../LevelEntity";
import UpgradeManager from "../util/UpgradeManager";
import Bullet from "./Bullet";
import TurretSoundGenerator from "./TurretSoundGenerator";

// Turret constants
const DEFAULT_FIRE_RATE = 0.08; // seconds between shots
const RAPID_FIRE_MULTIPLIER = 0.5; // multiplier for rapid fire upgrade
const SPRITE_STROKE_WIDTH = 2; // stroke width for turret rendering

// Damage constants
const BASE_DAMAGE = 1; // base bullet damage
const DAMAGE_BOOST_1_MULTIPLIER = 1.5; // 50% more damage
const DAMAGE_BOOST_2_MULTIPLIER = 2.0; // 100% more damage
const BULLET_SPEED = 500; // bullet speed in units per second

// Rotation constants
const ROTATION_LERP_FACTOR = 8; // higher = faster rotation response
const MAX_ROTATION_SPEED = 6; // maximum radians per second

// Turret visual shape constants
const TURRET_SIDES = 6;
const TURRET_RADIUS = 5;
const TURRET_BARREL_LENGTH = 10;
const TURRET_BARREL_HALF_WIDTH = 1;

/**
 * Create a turret shape - hexagon
 */
function createTurretShape(): PolygonShape {
  return {
    points: createRegularPolygon(TURRET_SIDES, TURRET_RADIUS),
    color: Colors.CYAN,
    secondaryColor: Colors.CYAN,
  };
}

/**
 * Create a turret barrel shape - simple rectangle
 */
function createTurretBarrelShape(): PolygonShape {
  return {
    points: [
      V(0, -TURRET_BARREL_HALF_WIDTH),
      V(TURRET_BARREL_LENGTH, -TURRET_BARREL_HALF_WIDTH),
      V(TURRET_BARREL_LENGTH, TURRET_BARREL_HALF_WIDTH),
      V(0, TURRET_BARREL_HALF_WIDTH),
    ],
    color: Colors.WHITE,
    secondaryColor: Colors.WHITE,
  };
}

export default class Turret extends LevelEntity implements Entity {
  private bodyRenderer: VectorSprite;
  private barrelRenderer: VectorSprite;
  private soundGenerator: TurretSoundGenerator;
  lastShotTime: number = 0;
  fireRate: number = DEFAULT_FIRE_RATE;

  // Rotation system
  currentRotation: number = 0;
  targetRotation: number = 0;

  constructor() {
    super();
    this.tags = ["turret"];

    // Get turret shapes
    const bodyShape = createTurretShape();
    const barrelShape = createTurretBarrelShape();

    // Create line segment renderers for visual representation
    this.bodyRenderer = VectorSprite.fromPolygon(
      bodyShape.points,
      bodyShape.color,
      SPRITE_STROKE_WIDTH,
    );

    this.barrelRenderer = VectorSprite.fromPolygon(
      barrelShape.points,
      barrelShape.color,
      SPRITE_STROKE_WIDTH,
    );

    // Add renderers as children
    this.addChild(this.bodyRenderer);
    this.addChild(this.barrelRenderer);

    // Create sound generator as a child entity
    this.soundGenerator = new TurretSoundGenerator();
    this.addChild(this.soundGenerator);
  }

  onRender(dt: number) {
    // Position renderers at turret location
    const position = this.getPosition();

    this.bodyRenderer.setPosition(position);
    this.barrelRenderer.setPosition(position);
  }

  onTick(dt: number) {
    if (!this.game) return;

    const mouseWorldPos = this.game.camera.toWorld(this.game.io.mousePosition);
    const direction = mouseWorldPos.sub(this.getPosition());

    // Update target rotation based on mouse position
    this.targetRotation = direction.angle;

    // Smooth rotation towards target with exponential easing and speed limit
    const angleDiff = angleDelta(this.currentRotation, this.targetRotation);
    const lerpFactor = 1 - Math.exp(-ROTATION_LERP_FACTOR * dt);
    const desiredRotationChange = angleDiff * lerpFactor;

    // Clamp rotation speed to maximum
    const maxRotationChange = MAX_ROTATION_SPEED * dt;
    const clampedRotationChange = clamp(
      desiredRotationChange,
      -maxRotationChange,
      maxRotationChange,
    );

    this.currentRotation += clampedRotationChange;

    // Apply rotation to both renderers
    this.bodyRenderer.setRotation(this.currentRotation);
    this.barrelRenderer.setRotation(this.currentRotation);

    // Apply rapid fire upgrade
    const upgradeManager = this.game.entities.getById(
      "upgradeManager",
    ) as UpgradeManager;
    const effectiveFireRate = upgradeManager?.has("rapidFire")
      ? this.fireRate * RAPID_FIRE_MULTIPLIER
      : this.fireRate;

    // Auto-fire - no need to hold mouse button
    // Only shoot if there are enemies
    const enemies = this.game.entities.getTagged("levelEntity");
    if (
      enemies.length > 0 &&
      this.game.elapsedTime - this.lastShotTime > effectiveFireRate
    ) {
      const shootDirection = polarToVec(
        this.currentRotation,
        direction.magnitude,
      );
      this.shoot(shootDirection);
      this.lastShotTime = this.game.elapsedTime;
    }
  }

  getPosition(): V2d {
    // Return position from the body renderer
    return this.bodyRenderer.getPosition();
  }

  shoot(direction: V2d) {
    // Calculate damage based on purchased upgrades
    const upgradeManager = this.game?.entities.getById(
      "upgradeManager",
    ) as UpgradeManager;
    let damageMultiplier = 1;

    if (upgradeManager?.has("damageBoost1")) {
      damageMultiplier = DAMAGE_BOOST_1_MULTIPLIER; // 50% more damage
    }
    if (upgradeManager?.has("damageBoost2")) {
      damageMultiplier = DAMAGE_BOOST_2_MULTIPLIER; // 100% more damage (replaces level 1)
    }

    const baseDamage = BASE_DAMAGE;
    const finalDamage = baseDamage * damageMultiplier;

    const bullet = new Bullet(
      this.getPosition(),
      direction,
      BULLET_SPEED,
      finalDamage,
    );

    // Add bullet to the current level
    this.getCurrentLevel()?.addChild(bullet);

    // Trigger sound effect
    this.soundGenerator.onShoot();
  }
}
